import React, { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';
import { ArrowLeft, RotateCcw } from 'lucide-react';

import { PAGES } from 'constants/pages';
import { MICROSTORE_COLUMN_TEMPLATE_STATE_LABELS, MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS } from '../constants/types';

import {
    GetMicrostoreColumnTemplateQuery,
    MicrostoreColumnTemplateState,
    MicrostoreColumnTemplateType,
    useUpdateMicrostoreColumnTemplateMutation,
} from 'data/__generated__';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { FullPageLoader } from 'components/FullPageLoader';
import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { TotemInput } from 'components/TotemInput';
import { TotemSelect } from 'components/TotemSelect';
import { ColumnShelfLocationDetailedEditor } from './ColumnShelfLocationDetailedEditor';
import { ColumnShelfLocationModernEditor } from './ColumnShelfLocationModernEditor';
import { LocationConfigSidebar } from './LocationConfigSidebar';
import { PrintColumnTemplate } from './PrintColumnTemplate';

import {
    mapMicrostoreColumnTemplateFormValuesToMicrostoreColumnTemplateUpdateInput,
    mapMicrostoreColumnTemplateToMicrostoreColumnTemplateFormValues,
    MicrostoreColumnTemplateFormValues,
} from './FormHelper/DataMapper';
import { AuditLogs } from 'components/AuditLogs';
import { MicrostoreColumnTemplateColumnsList } from './MicrostoreColumnTemplateColumnsList';

export const MicrostoreColumnTemplateDetailsForm = ({
    microstoreColumnTemplate,
}: {
    microstoreColumnTemplate: GetMicrostoreColumnTemplateQuery['microstoreColumnTemplateQuery'];
}) => {
    const [microstoreColumnTemplateFormValues, setMicrostoreColumnTemplateFormValues] =
        useState<MicrostoreColumnTemplateFormValues>(
            mapMicrostoreColumnTemplateToMicrostoreColumnTemplateFormValues({ microstoreColumnTemplate }),
        );
    const [isFormDirty, setIsFormDirty] = useState<boolean>(false);

    const [selectedShelfTemplateIndex, setSelectedShelfTemplateIndex] = useState<number | null>(null);
    const [selectedLocationTemplatePosition, setSelectedLocationTemplatePosition] = useState<{
        column: number;
        row: number;
    } | null>(null);

    // State pour contrôler l'ouverture/fermeture du sidebar de configuration
    const isSidebarOpen = selectedShelfTemplateIndex !== null && selectedLocationTemplatePosition !== null;

    const [updateMicrostoreColumnTemplate, { loading: updateLoading }] = useUpdateMicrostoreColumnTemplateMutation();

    function setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
        newMicrostoreColumnTemplateFormValues,
        newIsDirty = true,
    }: {
        newMicrostoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
        newIsDirty?: boolean;
    }) {
        setMicrostoreColumnTemplateFormValues(newMicrostoreColumnTemplateFormValues);
        setIsFormDirty(newIsDirty);
    }

    function resetFormValues() {
        setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
            newMicrostoreColumnTemplateFormValues: mapMicrostoreColumnTemplateToMicrostoreColumnTemplateFormValues({
                microstoreColumnTemplate,
            }),
            newIsDirty: false,
        });
    }

    async function onSubmit(e?: React.FormEvent<HTMLFormElement>) {
        e?.preventDefault();

        const microstoreColumnTemplateUpdateInput =
            mapMicrostoreColumnTemplateFormValuesToMicrostoreColumnTemplateUpdateInput({
                microstoreColumnTemplateFormValues,
            });

        const { data } = await updateMicrostoreColumnTemplate({
            variables: { microstoreColumnTemplate: microstoreColumnTemplateUpdateInput },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la modification du template de colonne');
        }

        const {
            updateMicrostoreColumnTemplateMutation: { newMicrostoreColumnTemplate, errors },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        if (newMicrostoreColumnTemplate) {
            toast.success(`Le template de colonne ${newMicrostoreColumnTemplate.name} a bien été modifié !`);
            setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
                newMicrostoreColumnTemplateFormValues: mapMicrostoreColumnTemplateToMicrostoreColumnTemplateFormValues({
                    microstoreColumnTemplate: newMicrostoreColumnTemplate,
                }),
                newIsDirty: false,
            });
        }
    }

    const typeOptions =
        Object.keys(MicrostoreColumnTemplateType)
            .map((type) => ({
                value: type as MicrostoreColumnTemplateType,
                label: MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS[type as MicrostoreColumnTemplateType],
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const stateOptions =
        Object.keys(MicrostoreColumnTemplateState)
            .map((state) => ({
                value: state as MicrostoreColumnTemplateState,
                label: MICROSTORE_COLUMN_TEMPLATE_STATE_LABELS[state as MicrostoreColumnTemplateState],
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Formulaire',
            items: [
                {
                    label: 'Réinitialiser',
                    icon: <RotateCcw />,
                    onClick: resetFormValues,
                    disabled: !isFormDirty,
                },
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/microstore-column-templates' }],
        },
    ];

    return (
        <>
            <Form onSubmit={onSubmit}>
                <Header>
                    <HeaderTitle>
                        <PageTitle page={PAGES.microstoreColumnTemplateDetails} />
                    </HeaderTitle>
                    <ButtonsContainer>
                        <PrintColumnTemplate microstoreColumnTemplateFormValues={microstoreColumnTemplateFormValues} />
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isFormDirty}
                            saveLoading={updateLoading}
                            saveTitle="Mettre à jour"
                            saveDataTest="template-save"
                        />
                    </ButtonsContainer>
                </Header>
                <Content>
                    <ModernEditorWrapper>
                        <EditorArea $sidebarOpen={isSidebarOpen}>
                            <SectionContainer title="Informations générales" isInitiallyOpen>
                                <ColumnsSectionContainer numberOfColumns={3}>
                                    <SectionColumn>
                                        <TotemInput
                                            label="Nom du template de colonne"
                                            placeholder="Frigo basique"
                                            dataTest="template-name"
                                            onChange={(newName) =>
                                                setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
                                                    newMicrostoreColumnTemplateFormValues: {
                                                        ...microstoreColumnTemplateFormValues,
                                                        name: newName,
                                                    },
                                                })
                                            }
                                            value={microstoreColumnTemplateFormValues.name}
                                        />
                                    </SectionColumn>
                                    <SectionColumn>
                                        <TotemSelect<MicrostoreColumnTemplateType>
                                            label="Type de colonne"
                                            placeholder="Type de colonne"
                                            value={microstoreColumnTemplateFormValues.type}
                                            options={typeOptions}
                                            onChange={(value) => {
                                                if (value) {
                                                    setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
                                                        newMicrostoreColumnTemplateFormValues: {
                                                            ...microstoreColumnTemplateFormValues,
                                                            type: value,
                                                        },
                                                    });
                                                }
                                            }}
                                        />
                                    </SectionColumn>
                                    <SectionColumn>
                                        <TotemSelect<MicrostoreColumnTemplateState>
                                            label="Statut"
                                            placeholder="Statut"
                                            value={microstoreColumnTemplateFormValues.state}
                                            options={stateOptions}
                                            onChange={(value) => {
                                                if (value) {
                                                    setMicrostoreColumnTemplateFormValuesAndIsFormDirty({
                                                        newMicrostoreColumnTemplateFormValues: {
                                                            ...microstoreColumnTemplateFormValues,
                                                            state: value,
                                                        },
                                                    });
                                                }
                                            }}
                                        />
                                    </SectionColumn>
                                </ColumnsSectionContainer>
                            </SectionContainer>

                            <SectionContainer title="MS Colonnes associées" isInitiallyOpen={false}>
                                <MicrostoreColumnTemplateColumnsList
                                    microstoreColumnTemplateName={microstoreColumnTemplate.name}
                                />
                            </SectionContainer>

                            <SectionContainer title="Création moderne" isInitiallyOpen>
                                <ColumnShelfLocationModernEditor
                                    microstoreColumnTemplateFormValues={microstoreColumnTemplateFormValues}
                                    setMicrostoreColumnTemplateFormValuesAndIsFormDirty={
                                        setMicrostoreColumnTemplateFormValuesAndIsFormDirty
                                    }
                                    selectedShelfTemplateIndex={selectedShelfTemplateIndex}
                                    setSelectedShelfTemplateIndex={setSelectedShelfTemplateIndex}
                                    selectedLocationTemplatePosition={selectedLocationTemplatePosition}
                                    setSelectedLocationTemplatePosition={setSelectedLocationTemplatePosition}
                                />
                            </SectionContainer>

                            <SectionContainer title="Création détaillée" isInitiallyOpen={false}>
                                <ColumnShelfLocationDetailedEditor
                                    microstoreColumnTemplateFormValues={microstoreColumnTemplateFormValues}
                                    setMicrostoreColumnTemplateFormValuesAndIsFormDirty={
                                        setMicrostoreColumnTemplateFormValuesAndIsFormDirty
                                    }
                                    selectedShelfTemplateIndex={selectedShelfTemplateIndex}
                                    setSelectedShelfTemplateIndex={setSelectedShelfTemplateIndex}
                                    selectedLocationTemplatePosition={selectedLocationTemplatePosition}
                                    setSelectedLocationTemplatePosition={setSelectedLocationTemplatePosition}
                                />
                            </SectionContainer>

                            <SectionContainer title="Audit logs" isInitiallyOpen={false}>
                                <AuditLogs collectionDocumentId={microstoreColumnTemplate._id} />
                            </SectionContainer>
                        </EditorArea>
                        {isSidebarOpen && (
                            <SidebarArea>
                                <LocationConfigSidebar
                                    isOpen={isSidebarOpen}
                                    microstoreColumnTemplateFormValues={microstoreColumnTemplateFormValues}
                                    selectedShelfTemplateIndex={selectedShelfTemplateIndex}
                                    selectedLocationTemplatePosition={selectedLocationTemplatePosition}
                                    setMicrostoreColumnTemplateFormValuesAndIsFormDirty={
                                        setMicrostoreColumnTemplateFormValuesAndIsFormDirty
                                    }
                                    onClose={() => setSelectedLocationTemplatePosition(null)}
                                />
                            </SidebarArea>
                        )}
                    </ModernEditorWrapper>
                </Content>
            </Form>

            {updateLoading ? <FullPageLoader /> : null}
        </>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100svh;
    min-height: 0;
`;

const ButtonsContainer = styled.div`
    display: flex;
    width: max-content;
    align-items: center;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const Content = styled.div`
    display: flex;
    width: 100%;
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow: hidden;
`;

const ModernEditorWrapper = styled.div`
    display: flex;
    width: 100%;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

const EditorArea = styled.div<{ $sidebarOpen: boolean }>`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 15px;
    overflow-x: auto;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 15px;
    }
`;

const SidebarArea = styled.div`
    width: 500px;
    height: 100%;
    flex-shrink: 0;
    overflow-y: auto;
    padding-top: 15px;
    padding-right: 15px;
    padding-bottom: 15px;
`;
